"use client";

import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import {
  MagnifyingGlass,
  X,
  Buildings,
  MapPin,
  Spinner,
} from "@phosphor-icons/react";

type Suggestion = {
  kind: "establishment" | "city";
  label: string;
  hint?: string;
  href: string;
};

type Props = {
  category: string;
  defaultValue?: string;
  /** Placeholder personnalisé selon la catégorie. */
  placeholder?: string;
};

export function EstablishmentSearch({
  category,
  defaultValue = "",
  placeholder,
}: Props) {
  const t = useTranslations("establishment");
  const finalPlaceholder = placeholder ?? t("search.placeholder", { singular: t(`categories.${category}.singular` as never) });
  const router = useRouter();
  const params = useSearchParams();
  const [value, setValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [, startTransition] = useTransition();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setValue(params.get("q") ?? "");
  }, [params]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    const q = value.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/establishments/search?category=${encodeURIComponent(category)}&q=${encodeURIComponent(q)}`,
          { signal: ctrl.signal },
        );
        if (!res.ok) {
          setSuggestions([]);
          return;
        }
        const data = (await res.json()) as { results: Suggestion[] };
        setSuggestions(data.results || []);
        setActiveIdx(-1);
      } catch (err) {
        if ((err as Error).name !== "AbortError") setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [value, category]);

  const grouped = useMemo(() => {
    const g: Record<Suggestion["kind"], Suggestion[]> = { establishment: [], city: [] };
    for (const s of suggestions) g[s.kind].push(s);
    const order: Suggestion["kind"][] = ["establishment", "city"];
    return order.filter((k) => g[k].length > 0).map((k) => ({ kind: k, items: g[k] }));
  }, [suggestions]);

  function pushSearch(q: string) {
    const sp = new URLSearchParams();
    if (q.trim()) sp.set("q", q.trim());
    startTransition(() => {
      router.push(`/${category}${sp.toString() ? `?${sp.toString()}` : ""}`);
    });
  }

  function navigate(href: string) {
    setOpen(false);
    startTransition(() => router.push(href));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setOpen(false);
    if (activeIdx >= 0 && suggestions[activeIdx]) navigate(suggestions[activeIdx].href);
    else pushSearch(value);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  }

  function clear() {
    setValue("");
    setSuggestions([]);
    pushSearch("");
    inputRef.current?.focus();
  }

  return (
    <div ref={containerRef} className="relative">
      <form onSubmit={onSubmit} autoComplete="off">
        <div className="relative">
          <MagnifyingGlass
            weight="bold"
            size={18}
            className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-[var(--sahha-text-500)]"
          />
          <input
            ref={inputRef}
            type="text"
            name={`${category}-search`}
            placeholder={finalPlaceholder}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            inputMode="search"
            autoComplete="off"
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            className="h-12 w-full rounded-full bg-white pl-11 pr-12 text-[14px] font-semibold text-[var(--sahha-text-900)] shadow-sm ring-1 ring-[var(--sahha-border-subtle)] placeholder:text-[var(--sahha-text-400)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-500)]"
          />
          {loading && (
            <Spinner
              size={16}
              weight="bold"
              className="absolute end-9 top-1/2 -translate-y-1/2 animate-spin text-[var(--sahha-text-400)]"
            />
          )}
          {value && (
            <button
              type="button"
              onClick={clear}
              aria-label={t("search.clear")}
              className="absolute end-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-[var(--sahha-text-500)] transition hover:bg-[var(--sahha-surface-1)]"
            >
              <X weight="bold" size={14} />
            </button>
          )}
        </div>
      </form>

      {open && value.trim().length >= 2 && (
        <div className="absolute start-0 end-0 z-30 mt-2 overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-[var(--sahha-border-subtle)]">
          {suggestions.length === 0 && !loading ? (
            <div className="px-4 py-6 text-center">
              <p className="text-[12.5px] font-bold text-[var(--sahha-text-700)]">{t("search.noSuggestion")}</p>
              <p className="mt-1 text-[11px] text-[var(--sahha-text-500)]">{t("search.pressEnter")}</p>
            </div>
          ) : (
            <ul className="max-h-[24rem] overflow-y-auto py-1">
              {grouped.map((group) => (
                <li key={group.kind}>
                  <p className="sticky top-0 bg-white px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--sahha-text-400)]">
                    {group.kind === "establishment" ? t("search.establishments") : t("search.cities")}
                  </p>
                  <ul>
                    {group.items.map((s) => {
                      const flat = suggestions.indexOf(s);
                      const isActive = flat === activeIdx;
                      return (
                        <li key={`${s.kind}-${s.label}-${s.href}`}>
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseEnter={() => setActiveIdx(flat)}
                            onClick={() => navigate(s.href)}
                            className={`flex w-full items-center gap-3 px-3 py-2 text-left transition ${
                              isActive ? "bg-[var(--sahha-primary-50)]" : "hover:bg-[var(--sahha-surface-1)]"
                            }`}
                          >
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-surface-1)] text-[var(--sahha-primary-600)]">
                              {s.kind === "city" ? <MapPin weight="duotone" size={16} /> : <Buildings weight="duotone" size={16} />}
                            </span>
                            <span className="min-w-0 flex-1">
                              <p className="truncate text-[13px] font-bold text-[var(--sahha-text-900)]">
                                {highlight(s.label, value)}
                              </p>
                              {s.hint && (
                                <p className="truncate text-[11px] text-[var(--sahha-text-500)]">{s.hint}</p>
                              )}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
              <li className="border-t border-[var(--sahha-border-subtle)]">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pushSearch(value)}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[12px] font-bold text-[var(--sahha-primary-700)] transition hover:bg-[var(--sahha-surface-1)]"
                >
                  <MagnifyingGlass size={13} weight="bold" />
                  {t("search.viewAllFor")}
                  <span className="font-extrabold">«&nbsp;{value.trim()}&nbsp;»</span>
                </button>
              </li>
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function highlight(text: string, query: string): React.ReactNode {
  const q = query.trim();
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded bg-[var(--sahha-primary-100)] px-0.5 text-[var(--sahha-primary-700)]">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}
